import { useLocation } from 'react-router-dom';

type To = string | ReturnType<typeof useLocation>;

/**
 * Custom hook that provides a function to preserve the jarvisRegion URL parameter
 * when navigating through the application.
 *
 * @returns A function that adds the current jarvisRegion to any navigation path
 */
export const useRegionalLink = (): ((to: To) => To) => {
  const location = useLocation();
  const currentSearchParams = new URLSearchParams(location.search);
  const jarvisRegion = currentSearchParams.get('jarvisRegion');

  /**
   * Adds the jarvisRegion parameter to the provided navigation target
   *
   * @param to - The navigation target (string URL or location object)
   * @returns The modified navigation target with jarvisRegion parameter
   */
  const generateRegionalPath = (to: To): To => {
    if (!jarvisRegion) {
      return to; // No region to append
    }

    let basePath = '';
    let searchParams: URLSearchParams;

    if (typeof to === 'string') {
      const [pathname, queryString = ''] = to.split('?');
      basePath = pathname;
      searchParams = new URLSearchParams(queryString);
    } else if (to?.pathname) {
      basePath = to.pathname;
      searchParams = new URLSearchParams(to.search || '');
    } else {
      // Unsupported 'to' format or no pathname
      return to;
    }

    searchParams.set('jarvisRegion', jarvisRegion);
    const newSearch = searchParams.toString();

    if (typeof to === 'string') {
      return basePath + (newSearch ? `?${newSearch}` : '');
    } else {
      // 'to' is an object
      return {
        ...to,
        pathname: basePath, // Ensure pathname is correctly set
        search: newSearch ? `?${newSearch}` : ''
      };
    }
  };

  return generateRegionalPath;
};
